import { PostgrestError } from '@supabase/supabase-js';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';

import { Button } from '../../../common/Button';
import { Dialog } from '../../../common/Dialog';
import { Tabs } from '../../../common/Tabs';
import { Spinner } from '../../../components/Spinner';
import { useAuth } from '../../../contexts/AuthContext';
import {
  getTabItems,
  initialCheckInData,
  MAX_VALIDATION_ATTEMPTS,
} from '../constants';
import { useGetAllCheckIns } from '../hooks/useGetAllCheckIns';
import { useGetCheckInBySession } from '../hooks/useGetCheckInsBySession';
import { useSubmitCheckIn } from '../hooks/useSubmitCheckIns';
import { getUserTickets } from '../queries/getUserTickets';
import { validateDescription } from '../services/validateDescription';
import {
  CheckInData,
  CheckInTypeEnum,
  TAssessmentResults,
  TicketOption,
} from '../types/types';
import { createPayload, validateForm } from '../utils';

import { CheckInForm } from './CheckInForm';
import { CheckInOverview } from './CheckInOverview';

export function CheckInTabs(): JSX.Element {
  // Set the selected tab based on time of day (after 3 PM = second tab)
  const [selectedTab, setSelectedTab] = useState(() => {
    const now = new Date();
    return now.getHours() >= 15 ? 1 : 0;
  });
  const [isFormValid, setIsFormValid] = useState(false);
  const { userData } = useAuth();
  const { id: userId, shadowId, shadowTo, activeSprint } = userData || {};
  const [validationCount, setValidationCount] = useState(0);
  const { data: TicketResponse = [], isLoading: loadingTickets } = useQuery<
    TicketOption[]
  >({
    queryKey: ['userTickets', userId, shadowTo, activeSprint?.id, selectedTab],
    queryFn: async () => {
      try {
        if (!userId || !activeSprint?.id)
          throw new Error('Missing required params');
        return await getUserTickets({
          userId,
          shadowTo, // shadow should get the tickets of the user
          sprintId: activeSprint.id,
        });
      } catch (error) {
        console.error('Error fetching user tickets:', error);
        throw error; // Re-throw to let useQuery handle it
      }
    },
    refetchOnWindowFocus: true,
    enabled: !!userId && !!activeSprint?.id,
  });
  const tickets = useMemo(() => {
    const computedTickets = TicketResponse.map((ticket) => ({
      ...ticket,
      strike:
        ticket.status === 'resolved' || ticket.completionPercentage === 100,
    }));
    return computedTickets.sort((a, b) => {
      return (a.completionPercentage || 0) - (b.completionPercentage || 0);
    });
  }, [TicketResponse]);
  const {
    data: { userCheckinId: checkInId, shadowCheckinIds } = {
      userCheckinId: null,
      shadowCheckinId: null,
      hasUserCheckin: false,
      hasShadowCheckin: false,
    },
    isLoading: loadingCheckIn,
  } = useGetCheckInBySession(userId, shadowId, selectedTab + 1);
  const { data: ticketCheckIns = [], isLoading: loadingTicketCheckIns } =
    useGetAllCheckIns(checkInId ? [checkInId] : null);
  const {
    data: shadowTicketCheckIns = [],
    isLoading: loadingShadowTicketCheckIns,
  } = useGetAllCheckIns(shadowCheckinIds || null);
  const [formState, setFormState] = useState<CheckInData>(initialCheckInData);
  const [isValidating, setIsValidating] = useState(false);
  const [assessments, setAssessments] = useState<TAssessmentResults>({
    results: [],
    currentAssessment: null,
  });
  const submitCheckInMutation = useSubmitCheckIn();

  const tabItems = useMemo(
    () => getTabItems(userData?.checkInTimes || []),
    [userData?.checkInTimes],
  );

  useEffect(() => {
    setFormState(initialCheckInData);
  }, [selectedTab]);

  useEffect(() => {
    const { isValid } = validateForm(formState);
    setIsFormValid(isValid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formState]);
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);

  const handleSubmit = async (): Promise<void> => {
    try {
      const { isValid, errorMessage } = validateForm(formState);
      if (validationCount < MAX_VALIDATION_ATTEMPTS) {
        try {
          setIsValidating(true);
          const assessmentResponse = await validateDescription(
            formState.description,
          );
          setIsValidating(false);
          setAssessments({
            results: assessments.currentAssessment
              ? [...assessments.results, assessments.currentAssessment]
              : assessments.results,
            currentAssessment: {
              ...assessmentResponse,
              isAiSuggestionAccepted: false,
              input: formState.description,
            },
          });
          setValidationCount(validationCount + 1);
          if (assessmentResponse && !assessmentResponse?.passed_assessment) {
            const isOnlyDetailsMissing = !Object.keys(assessmentResponse).some(
              (key) =>
                typeof assessmentResponse[
                  key as keyof typeof assessmentResponse
                ] === 'boolean' &&
                key !== 'should_contain_details_of_action_performed' &&
                key !== 'passed_assessment' &&
                assessmentResponse[key as keyof typeof assessmentResponse] ===
                  false,
            );
            if (isOnlyDetailsMissing) {
              setValidationCount(MAX_VALIDATION_ATTEMPTS);
              return;
            }
            return;
          }
        } catch (error) {
          setIsValidating(false);
          toast.error(
            'Our AI service is temporarily unavailable. Please have a self review before submitting the check-in!',
          );
          setValidationCount(MAX_VALIDATION_ATTEMPTS);
          return;
        }
      }
      if (!userId || !isValid) {
        if (errorMessage) toast.error(errorMessage);
        return;
      }
      if (
        formState.type === CheckInTypeEnum.TICKET &&
        !showConfirmationDialog &&
        formState.prevProgress === formState.progress
      ) {
        setShowConfirmationDialog(true);
        return;
      }

      const payload = createPayload(
        formState,
        userId,
        checkInId,
        selectedTab,
        tickets,
      );

      submitCheckInMutation.mutate(
        {
          data: payload,
          assessments: [
            ...assessments.results,
            ...(assessments.currentAssessment
              ? [assessments.currentAssessment]
              : []),
          ],
        },
        {
          onSuccess: () => {
            toast.success('Check-in submitted successfully');
            setFormState(initialCheckInData);
            setAssessments({
              results: [],
              currentAssessment: null,
            });
            setValidationCount(0);
          },
          onError: (error: Error | PostgrestError) => {
            if (error.message === '23505') {
              toast.error(
                'A check-in for this ticket already exists in this session, Please edit the existing check-in',
              );
              return;
            }
            const submitErrorMessage = 'Failed to submit check-in';
            toast.error(submitErrorMessage);
          },
          onSettled: () => {
            setShowConfirmationDialog(false);
          },
        },
      );
    } catch (error) {
      console.error('Unexpected error during submission:', error);
      toast.error('An unexpected error occurred ');
    }
  };
  if (
    loadingTickets ||
    loadingCheckIn ||
    loadingTicketCheckIns ||
    loadingShadowTicketCheckIns
  ) {
    return (
      <div className="flex flex-col items-center justify-center w-full h-[80vh] text-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Tabs
        items={tabItems}
        selectedIndex={selectedTab}
        onChange={setSelectedTab}
        className="w-full"
      />
      <Dialog
        isOpen={showConfirmationDialog}
        onClose={() => setShowConfirmationDialog(false)}
        title="Confirm Check-in"
      >
        <div className="flex flex-col gap-4">
          <p>
            The progress bar hasn&apos;t been updated. Do you want to continue
            without changes?
          </p>
          <div className="flex justify-end gap-4">
            <Button
              variant="primary"
              className="px-8"
              onClick={() => setShowConfirmationDialog(false)}
            >
              No
            </Button>
            <Button
              variant="secondary"
              className="px-8"
              onClick={() => {
                handleSubmit().catch(console.error);
              }}
            >
              Yes
            </Button>
          </div>
        </div>
      </Dialog>
      <div className="flex w-full mt-4 items-center gap-4 self-stretch">
        <CheckInForm
          formState={formState}
          setFormState={setFormState}
          tickets={tickets}
          onSubmit={handleSubmit}
          isSubmitting={submitCheckInMutation.isPending}
          isFormValid={isFormValid}
          session={selectedTab}
          assessment={assessments}
          setAssessment={setAssessments}
          isValidating={isValidating}
          setValidationCount={setValidationCount}
        />
        <CheckInOverview
          userId={userId}
          shadowId={shadowId}
          userCheckInId={checkInId}
          ticketCheckIns={ticketCheckIns}
          setFormState={setFormState}
          selectedTab={selectedTab}
          shadowTicketCheckIns={shadowTicketCheckIns}
        />
      </div>
    </>
  );
}
